import { Checkbox } from "@mui/material";
import MuiButton from "../../../components/basicComponent/MuiButton";
import MuiSearchSelect from "../../../components/basicComponent/MuiSearchSelect";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import React, { useEffect, useState } from "react";
import { MuiInput } from "../../../components/basicComponent/MuiInput";
import { useLocation, useNavigate } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useFormik } from "formik";

interface GuestDetailsCardProps {
  roomCount?: number;
  room?: [];
  initialData?: {
    guestData: any[];
    contact: { email: string; passport: string; mobile: string };
    gst: {
      gstNo: string;
      companyName: string;
      gstEmail: string;
      gstMobile: string;
      address: string;
    } | null; // ✅ allow null here
  };
  onContinue?: (guestInfo: {
    guestData: any[];
    contact: { email: string; passport: string; mobile: string };
    gst: {
      gstNo: string;
      companyName: string;
      gstEmail: string;
      gstMobile: string;
      address: string;
    } | null; // ✅ allow null in return payload as well
  }) => void;
}

const GuestDetailsCard: React.FC<GuestDetailsCardProps> = ({
  room,
  roomCount = 1,
  onContinue,
  initialData,
}) => {
  const [showCard, setShowCard] = useState(true);
  const [showGSTCard, setShowGSTCard] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isFormValid, setIsFormValid] = useState(false);

  const [guestData, setGuestData] = useState(
    initialData?.guestData ||
      Array.from({ length: roomCount }, () => ({
        title: "",
        firstName: "",
        lastName: "",
      }))
  );
  const [contact, setContact] = useState(
    initialData?.contact || { email: "", mobile: "", passport: "" }
  );
  const [gst, setGst] = useState(
    initialData?.gst || {
      gstNo: "",
      companyName: "",
      gstEmail: "",
      gstMobile: "",
      address: "",
    }
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleGuestChange = (index: number, key: string, value: string) => {
    const updated = [...guestData];
    (updated[index] as any)[key] = value;
    setGuestData(updated);
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [guestData, contact, agreeTerms]);
  const validateForm = (showErrors = false) => {
    const newErrors: { [key: string]: string } = {};
    const nameRegex = /^[a-zA-Z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{8,15}$/;

    // Guest fields
    guestData.forEach((guest: any, index: number) => {
      if (!guest.title) newErrors[`title-${index}`] = "Title is required";
      if (!guest.firstName || !nameRegex.test(guest.firstName)) {
        newErrors[`firstName-${index}`] = "First name should be valid";
      }
      if (!guest.lastName || !nameRegex.test(guest.lastName)) {
        newErrors[`lastName-${index}`] = "Last name should be valid";
      }
    });

    // Contact fields
    if (!contact.email || !emailRegex.test(contact.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!contact.mobile || !mobileRegex.test(contact.mobile)) {
      newErrors.mobile = "Enter a valid mobile number";
    }
    if (!contact.passport) {
      newErrors.passport = "Passport is required";
    }

    // GST validation - only if user filled any GST field
    const gstFilled = Object.values(gst).some((val) => val.trim() !== "");
    if (gstFilled) {
      if (!gst.gstNo) newErrors.gstNo = "GST No is required";
      if (!gst.companyName) newErrors.companyName = "Company Name is required";
      if (!gst.gstEmail || !emailRegex.test(gst.gstEmail)) {
        newErrors.gstEmail = "Enter a valid GST Email";
      }
      if (!gst.gstMobile || !mobileRegex.test(gst.gstMobile)) {
        newErrors.gstMobile = "Enter a valid GST Mobile";
      }
      if (!gst.address) newErrors.gstAddress = "Address is required";
    }

    if (showErrors) {
      setErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    const gstFilled = Object.values(gst).some((val) => val.trim() !== "");
    if (validateForm(true)) {
      // Trim guest data
      const trimmedGuestData = guestData.map((guest) => ({
        title: guest.title?.trim() || "",
        firstName: guest.firstName?.trim() || "",
        lastName: guest.lastName?.trim() || "",
        children: guest?.children?.map((child: any) => ({
          title: child.title?.trim() || "",
          firstName: child.firstName?.trim() || "",
          lastName: child.lastName?.trim() || "",
        })),
      }));

      // Trim contact data
      const trimmedContact = {
        email: contact.email?.trim() || "",
        passport: contact.passport?.trim() || "",
        mobile: contact.mobile?.trim() || "",
      };

      // Trim GST data only if filled
      const trimmedGst = gstFilled
        ? {
            gstNo: gst.gstNo?.trim() || "",
            companyName: gst.companyName?.trim() || "",
            gstEmail: gst.gstEmail?.trim() || "",
            gstMobile: gst.gstMobile?.trim() || "",
            address: gst.address?.trim() || "",
          }
        : null;

      onContinue?.({
        guestData: trimmedGuestData,
        contact: trimmedContact,
        gst: trimmedGst,
      });
    }
  };

  const handleChildGuestChange = (
    roomIndex: number,
    fieldOrChildIndex: string | number,
    field?: string,
    value?: string
  ) => {
    const updatedGuests = [...guestData];

    // If fieldOrChildIndex is a number → it's a child
    if (typeof fieldOrChildIndex === "number" && field && value !== undefined) {
      const childIndex = fieldOrChildIndex;

      // Ensure room exists
      if (!updatedGuests[roomIndex]) {
        updatedGuests[roomIndex] = { children: [] };
      }

      // Ensure children array exists
      if (!updatedGuests[roomIndex].children) {
        updatedGuests[roomIndex].children = [];
      }

      // Ensure specific child exists
      if (!updatedGuests[roomIndex].children[childIndex]) {
        updatedGuests[roomIndex].children[childIndex] = {
          title: "",
          firstName: "",
          lastName: "",
        };
      }

      updatedGuests[roomIndex].children[childIndex][field] = value;
    } else if (typeof fieldOrChildIndex === "string") {
      // It's an adult guest
      const field = fieldOrChildIndex;
      updatedGuests[roomIndex][field] = value;
    }
    setGuestData(updatedGuests);
  };

  const roomArray = Array.from({ length: roomCount }, (_, i) => i + 1);

  return (
    <>
      <div className="card card-custom my-3">
        <div className="card-header card-head-bg">
          <span>
            <PeopleOutlineOutlinedIcon /> Guest Details
          </span>
        </div>
        <div className="card-body">
          <div className="card">
            <div className="card-body">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="">
                  Adults{" "}
                  {Array.isArray(location.state?.payload?.Guests?.Room)
                    ? location.state.payload.Guests.Room.reduce(
                        (sum: number, r: any) => sum + (r?.NumberOfAdults || 0),
                        0
                      )
                    : guestData.length}
                </h3>
                <span
                  className="cursor-pointer"
                  onClick={() => setShowCard(!showCard)}
                >
                  {showCard ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </span>
              </div>

              {showCard && (
                <>
                  <>
                    {room?.map((room: any, roomIndex) => (
                      <div key={roomIndex} className="room-section">
                        {/* <h6 className="mt-4">Room {roomIndex + 1} - Children: {room.Child.NumberOfChild}</h6> */}
                        <div className="row mt-4" key={roomIndex}>
                          <div className="col-md-1">
                            <div className="room-circle">
                              Room {roomIndex + 1}
                            </div>
                          </div>
                          <div className="col-md-3 margin-bottom-class">
                            <MuiSearchSelect
                              label="Title"
                              placeholder="Select Title"
                              options={[
                                { id: "Mr", label: "Mr", value: "Mr" },
                                { id: "Ms", label: "Ms", value: "Ms" },
                              ]}
                              value={guestData[roomIndex].title}
                              onChange={(value) =>
                                handleGuestChange(roomIndex, "title", value)
                              }
                              error={!!errors[`title-${roomIndex}`]}
                              helperText={errors[`title-${roomIndex}`]}
                            />
                          </div>
                          <div className="col-md-4 margin-bottom-class margin-bottom-class">
                            <MuiInput
                              placeholder="First Name"
                              label="First Name"
                              value={guestData[roomIndex].firstName}
                              onChange={(value) =>
                                handleGuestChange(roomIndex, "firstName", value)
                              }
                              error={!!errors[`firstName-${roomIndex}`]}
                              helperText={errors[`firstName-${roomIndex}`]}
                            />
                          </div>
                          <div className="col-md-4 margin-bottom-class margin-bottom-class">
                            <MuiInput
                              placeholder="Last Name"
                              label="Last Name"
                              value={guestData[roomIndex].lastName}
                              onChange={(value) =>
                                handleGuestChange(roomIndex, "lastName", value)
                              }
                              error={!!errors[`lastName-${roomIndex}`]}
                              helperText={errors[`lastName-${roomIndex}`]}
                            />
                          </div>
                        </div>
                        {[...Array(room.Child.NumberOfChild)].map(
                          (_, childIndex) => (
                            <div key={childIndex} className="row mb-3">
                              <div className="col-md-1 d-flex align-items-center">
                                <div className="child-circle">
                                  Child {childIndex + 1}
                                </div>
                              </div>
                              <div className="col-md-6 col-lg-3">
                                <MuiSearchSelect
                                  label="Title"
                                  placeholder="Select Title"
                                  options={[
                                    { id: "Mr", label: "Master", value: "Mr" },
                                    { id: "Ms", label: "Miss", value: "Ms" },
                                  ]}
                                  value={
                                    guestData[roomIndex]?.children?.[childIndex]
                                      ?.title || ""
                                  }
                                  onChange={(value) =>
                                    handleChildGuestChange(
                                      roomIndex,
                                      childIndex,
                                      "title",
                                      value
                                    )
                                  }
                                  error={
                                    !!errors[`title-${roomIndex}-${childIndex}`]
                                  }
                                  helperText={
                                    errors[`title-${roomIndex}-${childIndex}`]
                                  }
                                />
                              </div>
                              <div className="col-md-6 col-lg-4">
                                <MuiInput
                                  placeholder="First Name"
                                  label="First Name"
                                  value={
                                    guestData[roomIndex]?.children?.[childIndex]
                                      ?.firstName || ""
                                  }
                                  onChange={(value) =>
                                    handleChildGuestChange(
                                      roomIndex,
                                      childIndex,
                                      "firstName",
                                      value
                                    )
                                  }
                                  error={
                                    !!errors[
                                      `firstName-${roomIndex}-${childIndex}`
                                    ]
                                  }
                                  helperText={
                                    errors[
                                      `firstName-${roomIndex}-${childIndex}`
                                    ]
                                  }
                                />
                              </div>
                              <div className="col-md-6 col-lg-4">
                                <MuiInput
                                  placeholder="Last Name"
                                  label="Last Name"
                                  value={
                                    guestData[roomIndex]?.children?.[childIndex]
                                      ?.lastName || ""
                                  }
                                  onChange={(value) =>
                                    handleChildGuestChange(
                                      roomIndex,
                                      childIndex,
                                      "lastName",
                                      value
                                    )
                                  }
                                  error={
                                    !!errors[
                                      `lastName-${roomIndex}-${childIndex}`
                                    ]
                                  }
                                  helperText={
                                    errors[
                                      `lastName-${roomIndex}-${childIndex}`
                                    ]
                                  }
                                />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </>

                  <div className="row mt-4">
                    <div className="col-md-12 fw-bold mb-2">
                      Contact Details
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-4 margin-bottom-class">
                        <MuiInput
                          label="Email Address"
                          placeholder="Email Address"
                          value={contact.email}
                          onChange={(value) =>
                            setContact({ ...contact, email: value })
                          }
                          error={!!errors.email}
                          helperText={errors.email}
                        />
                      </div>
                      <div className="col-md-4 margin-bottom-class">
                        <PhoneInput
                          value={contact.mobile}
                          onChange={(value: any) =>
                            setContact({ ...contact, mobile: value })
                          }
                          inputStyle={{ width: "100%" }}
                          country={""} // Or "ae" if UAE should be default
                          preferredCountries={["ae", "in", "us"]}
                          enableSearch
                          placeholder="Enter mobile number" // ✅ Placeholder here
                          inputProps={{
                            name: "mobile",
                            required: true,
                          }}
                          // isValid={(value: any) => {
                          //   const mobileRegex = /^[0-9]{8,15}$/;
                          //   if (
                          //     !mobileRegex.test(
                          //       value.replace(/^\+?\d{1,4}/, "")
                          //     )
                          //   ) {
                          //     return "Enter a valid mobile number";
                          //   }
                          //   return true;
                          // }}
                        />
                      </div>
                      <div className="col-md-4">
                        <MuiInput
                          placeholder="Passport No"
                          label="Passport No"
                          value={contact.passport}
                          onChange={(value) =>
                            setContact({ ...contact, passport: value })
                          }
                          error={!!errors.passport}
                          helperText={errors.passport}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="row ms-2">
            <div
              className="col-md-12 showmore Plu mt-2 mb-2"
              onClick={() => setShowGSTCard(!showGSTCard)}
            >
              {showGSTCard ? "(-)" : "(+)"} GST Details (Optional)
            </div>
            {showGSTCard && (
              <div className="row mb-3">
                <div className="col-md-4 margin-bottom-class">
                  <MuiInput
                    label="GST No"
                    placeholder="Enter GST No"
                    value={gst.gstNo}
                    onChange={(value) => setGst({ ...gst, gstNo: value })}
                  />
                </div>
                <div className="col-md-4 margin-bottom-class">
                  <MuiInput
                    placeholder="Company Name"
                    label="Company Name"
                    value={gst.companyName}
                    onChange={(value) => setGst({ ...gst, companyName: value })}
                  />
                </div>
                <div className="col-md-4 margin-bottom-class">
                  <MuiInput
                    placeholder="GST Email"
                    label="GST Email"
                    value={gst.gstEmail}
                    onChange={(value) => setGst({ ...gst, gstEmail: value })}
                    error={!!errors.gstEmail}
                    helperText={errors.gstEmail}
                  />
                </div>
                <div className="col-md-4 mt-3">
                  <PhoneInput
                    value={gst.gstMobile}
                    onChange={(value: any) =>
                      setGst({ ...gst, gstMobile: value })
                    }
                    placeholder="Enter GST Mobile"
                    inputStyle={{ width: "100%" }}
                    country={""} // ✅ UAE as default
                    preferredCountries={["ae", "in", "us"]}
                    enableSearch
                    inputProps={{
                      name: "mobile",
                      required: true,
                    }}
                    // isValid={(value: any) => {
                    //   const mobileRegex = /^[0-9]{8,15}$/;
                    //   if (!mobileRegex.test(value.replace(/^\+?\d{1,4}/, ""))) {
                    //     return "Enter a valid mobile number";
                    //   }
                    //   return true;
                    // }}
                  />
                </div>
                <div className="col-md-8 mt-3">
                  <MuiInput
                    placeholder="Address"
                    label="Address"
                    value={gst.address}
                    onChange={(value) => setGst({ ...gst, address: value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* <div className="row">
            <div className="d-flex align-items-center">
              <Checkbox
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
              />
              <span>
                I understand and agree to the rules of this fare, the
                <span className="showmore">Terms & Conditions</span> and
                <span className="showmore">Privacy Policy</span> of EWTS.
              </span>
            </div>
          </div> */}
        </div>
      </div>

      <div className="user-button mt-4">
        <MuiButton
          className={`main-btn ${!isFormValid ? "disabled-btn" : ""}`}
          label="Continue to Payment"
          type="button"
          onClick={handleContinue}
          // disabled={!agreeTerms || !isFormValid}
        />
      </div>
    </>
  );
};

export default GuestDetailsCard;
